import React, {FC, PropsWithChildren, useMemo, useSyncExternalStore} from 'react';
import {ThemeProvider} from 'styled-components';

import {globalWindow} from '../../../../libs/shared/core/src/helpers/browser/browserHelpers';
import {prefersDarkMode$} from '../../../../libs/shared/core/src/helpers/environment/darkMode';
import {expandedDarkTheme} from '../../../../libs/shared/ui/src/theme/dark/darkTheme';
import {lightTheme} from '../../../../libs/shared/ui/src/theme/light/lightTheme';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {UserThemesEnum} from '../../../../libs/shared/ui/src/theme/userThemesEnum';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {getTheme} from '../../../store/data/preferences/preferencesSelectors';

const subscribeToPrefersDarkMode = (callback: () => void) => {
  const subscription = prefersDarkMode$.subscribe(callback);
  return () => subscription.unsubscribe();
};

const getPrefersDarkModeSnapshot = () => globalWindow.matchMedia('(prefers-color-scheme: dark)').matches;

export const ExpandedDarkThemeProvider: FC<PropsWithChildren<unknown>> = ({children}) => {
  const theme = useExpandedDarkTheme();

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

export function useExpandedDarkTheme(): UserTheme {
  const globalTheme = useUserTheme();
  const userThemeSetting = useAppSelector(getTheme);
  const isSystemThemeSetting = userThemeSetting === UserThemesEnum.SYSTEM;
  const prefersDarkMode = useSyncExternalStore(subscribeToPrefersDarkMode, getPrefersDarkModeSnapshot);

  return useMemo(() => {
    // If we don't have the auto switch boolean => use the expanded dark theme if needed
    if (!isSystemThemeSetting) {
      return userThemeSetting === UserThemesEnum.DARK ? expandedDarkTheme : globalTheme;
    }

    // Otherwise check for prefers-color-scheme: dark
    return prefersDarkMode ? expandedDarkTheme : lightTheme;
  }, [globalTheme, prefersDarkMode, isSystemThemeSetting, userThemeSetting]);
}
